import { useEffect, useRef, useState } from "react";
import * as uuid from "uuid";
import styles from "../../../../../../styles/add-product.module.scss";
import { Button } from "../../../../../components/Button";
import { Checkbox } from "../../../../../components/Checkbox";
import { CurrencyInput } from "../../../../../components/CurrencyInput";
import { Drawer } from "../../../../../components/Drawer";
import { Hairline } from "../../../../../components/Hairline";
import { Input } from "../../../../../components/Input";
import { Label } from "../../../../../components/Label";
import { Message } from "../../../../../components/Message";
import { Radio } from "../../../../../components/Radio";
import { Select } from "../../../../../components/Select";
import { Textarea } from "../../../../../components/Textarea";
import { If } from "../../../../../components/control-statements/If";
import { Icon } from "../../../../../components/core/Icon";

import { AssetsAPI } from "../../../../../../providers/AssetsAPI";
import { NumberInput } from "../../../../../components/NumberInput";
import { EnvironmentStorage } from "shared/tools/EnvironmentStorage";

import classNames from "classnames/bind";
import {
  ConnectStep,
  ConnectStep1,
  ConnectStep2,
  SuccessIcon,
} from "shared/components/Icons";
import { Add } from "shared/components/Add";
import Toggle2 from "shared/components/Toggle2";
import { CatalogAPI } from "providers/CatalogAPI";
const css = classNames.bind(styles);

const getTotalCharacters = (product) => {
  return `${product?.sku?.length > 0 ? product?.sku?.length : 0}/60`;
};

const OptionItem = ({
  option,
  question,
  product,
  optionIndex,
  onChange,
  onDelete,
  handleOptionSteps,
  sortAnswers,
  moveUp,
  moveDown,
  isLast,
}) => {
  const [useDescription, setUseDescription] = useState(option.description);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (sortAnswers) setCollapsed(false);
  }, [sortAnswers]);

  useEffect(() => {
    if (!useDescription) onChange({ ...option, description: null });
  }, [useDescription]);

  const handleImage = async (e) => {
    const image = await handleSetImage(e);

    if (image) onChange({ ...option, image });
  };

  const handleSetImage = async (e) => {
    return new Promise((resolve) => {
      const file = e.target.files[0];

      if (file) {
        const maxFileSize = 1;
        const fileSize = file.size / 1024 / 1024;
        const fileType = file.type;

        if (fileSize > maxFileSize) {
          alert({ text: `Escolha um arquivo com no máximo ${maxFileSize}MB` });
          return resolve(false);
        }

        const pattern = /image-*/;
        if (!fileType.match(pattern)) {
          alert({ text: "Arquivo inválido." });
          return resolve(false);
        }

        let reader = new FileReader();
        reader.onload = async (readerEvent) => {
          const imageSrc = readerEvent.target.result;
          const img = new Image();

          img.onload = async () => {
            if (img.width > 500 || img.height > 500) {
              alert({
                text: "A imagem deve ter dimensões de até 500x500",
              });
              return resolve(false);
            }

            const response = await AssetsAPI.PostAssets({
              mime_type: fileType,
              file: imageSrc.split("base64,")[1],
            });

            const image = response.getData(null);
            return resolve(image);
          };

          img.src = imageSrc;
        };

        reader.readAsDataURL(file);
      }

      e.target.value = null;
    });
  };

  const hasMatchingRefId = (array, targetObject) => {
    const targetRefId = targetObject.ref_id;

    const checkRefId = (items) => {
      for (let item of items) {
        if (item.ref_id === targetRefId) return true;

        if (item.items && checkRefId(item.items)) return true;
      }
      return false;
    };

    return checkRefId(array);
  };

  const questionExists = hasMatchingRefId(product.items, option);

  return (
    <div className="d-flex align-center">
      <div className={styles.optionItem}>
        <div
          className="d-flex align-center justify-between mb-20"
          onClick={() => setCollapsed(!collapsed)}
        >
          <Label className="mb-0">Resposta {optionIndex + 1}</Label>
          <If cond={!sortAnswers}>
            <div className={styles.chevron}>
              <i className="far fa-chevron-down" />
            </div>
          </If>
        </div>

        <div className="d-flex align-center">
          <label
            className={css({ picture: true, disabled: sortAnswers })}
            htmlFor={option.id}
          >
            <input
              type="file"
              id={option.id}
              accept="image/*"
              onChange={handleImage}
            />
            {option.image ? (
              <img
                src={`${process.env.REACT_APP_ASSETS_URL}/${option.image}`}
                alt=""
              />
            ) : (
              <>
                <Icon name="image" light />
                <span className={styles.plus}>
                  <Icon name="plus" size="xs" />
                </span>
              </>
            )}
          </label>
          <Input
            design="two"
            className="w-100 ml-15"
            disabled={sortAnswers}
            defaultValue={option.name}
            onChange={(e) => {
              onChange({ ...option, name: e.target.value });
            }}
          />
          <If cond={!sortAnswers}>
            <Button
              design="default-icon"
              className="ml-15"
              onClick={() => onDelete(option, optionIndex)}
            >
              <Icon name="trash" light size="xl" />
            </Button>
          </If>
        </div>

        <If cond={collapsed}>
          <div className="mt-15">
            {useDescription && (
              <Textarea
                design="three"
                placeholder="Descrição"
                value={option.description}
                minHeight={100}
                onChange={(e) => {
                  onChange({ ...option, description: e.target.value });
                }}
              />
            )}

            <Input
              label="Código SKU"
              design="three"
              placeholder="----"
              className="mt-15"
              maxLength={60}
              suffix2={getTotalCharacters(option)}
              value={option.sku}
              onChange={(e) => onChange({ ...option, sku: e.target.value })}
            />

            <div className="d-flex align-center mt-25">
              {!option?.promo_price && (
                <CurrencyInput
                  design="three"
                  value={option.price}
                  onChange={(e, m, f) => {
                    onChange({ ...option, price: f });
                  }}
                  placeholder="R$ 0,00"
                  className="w-100"
                  label="Preço"
                />
              )}
              {!!option?.promo_price && (
                <>
                  <CurrencyInput
                    design="three"
                    value={option.old_price || option.price}
                    onChange={(e, m, f) => {
                      onChange({ ...option, old_price: f });
                    }}
                    placeholder="R$ 0,00"
                    className="w-100"
                    label="Preço inicial"
                  />
                  <CurrencyInput
                    design="three"
                    value={option.price}
                    onChange={(e, m, f) => onChange({ ...option, price: f })}
                    placeholder="R$ 0,00"
                    className="w-100 ml-15"
                    label="Preço promocional"
                  />
                </>
              )}
            </div>

            <Hairline className="mt-15 mb-15" />

            <Checkbox
              checked={useDescription}
              className="mb-10"
              design="two"
              onChange={() => setUseDescription(!useDescription)}
            >
              Utilizar descrição
            </Checkbox>
            <Checkbox
              checked={!!option.promo_price}
              design="two"
              onChange={(e) =>
                onChange({
                  ...option,
                  promo_price: e,
                  old_price: !e ? null : option.price,
                  price: !e ? option.old_price : option.price,
                })
              }
            >
              Utilizar promoção
            </Checkbox>
            {!!questionExists ? (
              <Button
                onClick={() => handleOptionSteps(option)}
                className="mt-10 w-100"
              >
                Adicionar etapas
              </Button>
            ) : null}
          </div>
        </If>
      </div>

      <If cond={sortAnswers}>
        <div className={styles.sort}>
          <button
            className={css({ disabled: isLast })}
            onClick={() => moveDown(option, optionIndex)}
          >
            <i className="fal fa-chevron-down" />
          </button>

          <button
            className={css({ disabled: optionIndex == 0 })}
            onClick={() => moveUp(option, optionIndex)}
          >
            <i className="fal fa-chevron-up" />
          </button>
        </div>
      </If>
    </div>
  );
};

const QuestionItem = ({
  mode,
  question,
  onEditMode,
  onDelete,
  onUpPosition,
  onDownPosition,
  currentPosition,
  totalItems,
  onClearStepConfig,
}) => {
  const canUpdate = !question.link_id;

  return (
    <>
      <If cond={mode == "sort"}>
        <div className="mb-10 d-flex">
          <div className="d-flex align-center flex-1 justify-between">
            <div className={`${styles.stepName}`}>{question.name}</div>
            <If cond={!canUpdate}>
              <i
                className="far fa-link"
                style={{ color: "#426CDA", fontSize: 10 }}
              />
            </If>
          </div>
          <Button
            design="default-icon"
            disabled={currentPosition == 0}
            onClick={() => onUpPosition(question)}
          >
            <Icon name="chevron-up" size="1x" />
          </Button>
          <Button
            design="default-icon"
            disabled={currentPosition == totalItems - 1}
            onClick={() => onDownPosition(question)}
            className="ml-5"
          >
            <Icon name="chevron-down" size="1x" />
          </Button>
        </div>
      </If>

      <If cond={mode == "show"}>
        <div className="mb-10 d-flex">
          <div className={`${styles.stepName} flex-1 justify-between`}>
            <span>{question.name}</span>
            <If cond={!canUpdate}>
              <i
                className="far fa-link"
                style={{ color: "#426CDA", fontSize: 10 }}
              />
            </If>
          </div>
          <Button
            design="default-icon"
            disabled={!canUpdate}
            onClick={() => {
              if (true) onEditMode(question);
              onClearStepConfig();
            }}
          >
            <Icon name="pen" size="xl" />
          </Button>
          <Button
            design="default-icon"
            className="ml-5"
            onClick={() => onDelete(question)}
          >
            <Icon name="trash" size="xl" />
          </Button>
        </div>
      </If>
    </>
  );
};

const Answers = ({
  question,
  catalog,
  product,
  holdingId,
  onChange,
  onChangeQuestion,
  handleOptionSteps,
  onClose,
}) => {
  const [selectedCategoryId, setSelectedCategoryId] = useState(
    catalog.items[0]?.id || null
  );
  const [sortAnswers, setSortAnswers] = useState(false);
  const [isRequired, setIsRequired] = useState(true);

  const [answerType, setAnswerType] = useState(
    question.items.length > 0 ? "withText" : null
  );
  const [tempQuestion, setTempQuestion] = useState({
    ...question,
    price_method: question.price_method || "SUM",
    min: question.min ?? 1,
    max: question.max ?? 1,
  });

  const showFractionConfig = [1747, 2276].includes(+holdingId);

  const originalQuestionRef = useRef(null);

  useEffect(() => {
    if (question.min > 0) setIsRequired(true);
    else setIsRequired(false);
  }, [question]);

  return (
    <div className={styles.answersScroll}>
      <div className={css({ question: true, notAllowed: sortAnswers })}>
        <Input
          label="Pergunta"
          design="two"
          placeholder="Ex. Deseja por molho?"
          defaultValue={tempQuestion.name}
          onChange={(e) =>
            setTempQuestion((question) => ({
              ...question,
              name: e.target.value,
            }))
          }
        />

        <Input
          label="Código SKU"
          design="two"
          placeholder="----"
          className="mt-15 mb-15"
          maxLength={60}
          suffix2={getTotalCharacters(tempQuestion)}
          value={tempQuestion.sku}
          onChange={(e) =>
            setTempQuestion((question) => ({
              ...question,
              sku: e.target.value,
            }))
          }
        />

        <div className={styles.minMax}>
          <Label>Obrigatoriedade das respostas</Label>
          <Hairline className="mt-10 mb-15" />
          <Radio
            family="required"
            className="mb-10"
            value={true}
            checked={isRequired}
            onChange={() => {
              setTempQuestion((question) => ({
                ...question,
                min: 1,
                max: question.max >= 1 ? question.max : 1,
              }));
              setIsRequired(true);
            }}
          >
            Obrigatório
          </Radio>
          <Radio
            family="required"
            value={false}
            checked={isRequired}
            onChange={() => {
              setTempQuestion((question) => ({
                ...question,
                min: 0,
                max: question.max >= 0 ? question.max : 0,
              }));
              setIsRequired(false);
            }}
          >
            Opcional
          </Radio>
          <div className={styles.spinners}>
            <div className="d-flex flex-column flex-1">
              <Label>Mínimo de opções</Label>
              <div className={styles.quantitySpinner}>
                <div
                  className={css({
                    minus: true,
                    disabled: isRequired
                      ? tempQuestion.min === 1
                      : tempQuestion.min === 0,
                  })}
                  onClick={() =>
                    setTempQuestion((question) => ({
                      ...question,
                      min: question.min - 1,
                    }))
                  }
                >
                  <i className="far fa-minus" />
                </div>

                <p className={styles.quantity}>{tempQuestion.min}</p>

                <div
                  className={styles.plus}
                  onClick={() =>
                    setTempQuestion((question) => ({
                      ...question,
                      min: question.min + 1,
                      max: question.min + 1,
                    }))
                  }
                >
                  <i className="far fa-plus" />
                </div>
              </div>
            </div>
            <div className="d-flex flex-column flex-1">
              <Label>Máximo de opções</Label>
              <div className={styles.quantitySpinner}>
                <div
                  className={css({
                    minus: true,
                    disabled:
                      (isRequired
                        ? tempQuestion.max === 1
                        : tempQuestion.max === 0) ||
                      tempQuestion.max <= tempQuestion.min,
                  })}
                  onClick={() =>
                    setTempQuestion((question) => ({
                      ...question,
                      max: question.max - 1,
                    }))
                  }
                >
                  <i className="far fa-minus" />
                </div>

                <p className={styles.quantity}>{tempQuestion.max}</p>

                <div
                  className={styles.plus}
                  onClick={() =>
                    setTempQuestion((question) => ({
                      ...question,
                      max: question.max + 1,
                    }))
                  }
                >
                  <i className="far fa-plus" />
                </div>
              </div>
            </div>
          </div>

          <Hairline className="mt-15 mb-15" />

          <Toggle2
            title="Permitir várias seleções de um mesmo item"
            checked={tempQuestion.mode == "quantity"}
            reverse
            onChange={() =>
              setTempQuestion((question) => ({
                ...question,
                mode: question.mode === "quantity" ? "option" : "quantity",
              }))
            }
          />
        </div>

        {showFractionConfig && (
          <div class="mb-20">
            <Checkbox
              name={`quantity_type_${Date.now()}`}
              design="two"
              checked={tempQuestion.quantityType === "fraction"}
              data-tooltip="Cliques"
              onChange={(value) =>
                setTempQuestion((question) => ({
                  ...question,
                  quantityType: value ? "fraction" : "default",
                }))
              }
              tooltip="Configuração interna para indicar que os itens da etapa são frações de um produto. Utilizado na integração com o Atena.ai"
            >
              Fracionado
            </Checkbox>
          </div>
        )}

        <div className={styles.minMax}>
          <Label className="mb-10">Cálculo de preço</Label>
          <div class="d-flex align-center">
            <Select
              design="one"
              className="flex-1"
              value={tempQuestion.price_method}
              options={[
                { value: "SUM", label: "Soma das etapas" },
                { value: "MAX", label: "Máximo das etapas" },
              ]}
              onChange={(e) => {
                setTempQuestion((question) => ({
                  ...question,
                  price_method: e.target.value,
                }));
              }}
            />
          </div>
        </div>
      </div>

      <Hairline className="mt-20 mb-20" />

      <div className={styles.items}>
        <div className="d-flex align-center justify-between mb-20">
          <Label className="mb-0">Respostas</Label>
          <If cond={tempQuestion.items.length}>
            <span className={styles.qty}>({tempQuestion.items.length})</span>
          </If>
        </div>

        <If cond={answerType === "withText"}>
          {tempQuestion.items.map((option, optionIndex) => (
            <OptionItem
              option={option}
              product={product}
              question={question}
              key={`option-${option.ref_id}`}
              optionIndex={optionIndex}
              onChange={(option) => {
                tempQuestion.items[optionIndex] = option;

                setTempQuestion((question) => ({
                  ...question,
                  ...tempQuestion,
                }));
              }}
              onDelete={(option, oIndex) => {
                let tempItems = tempQuestion.items;
                tempItems = tempQuestion.items.filter((o, oIdx) => {
                  return oIdx !== oIndex;
                });

                setTempQuestion((question) => ({
                  ...question,
                  items: tempItems,
                }));
                if (tempQuestion.items.length == 0) {
                  setAnswerType(null);
                }
              }}
              handleOptionSteps={(option) => handleOptionSteps(option)}
              sortAnswers={sortAnswers}
              moveUp={(option, oIndex) => {
                let tempItems = tempQuestion.items;
                let item = tempItems[oIndex];
                tempItems[oIndex] = tempItems[oIndex - 1];
                tempItems[oIndex - 1] = item;
                setTempQuestion((question) => ({
                  ...question,
                  items: tempItems.map((item, index) => ({
                    ...item,
                    position: index,
                  })),
                }));
              }}
              moveDown={(option, oIndex) => {
                let tempItems = tempQuestion.items;
                let item = tempItems[oIndex];
                tempItems[oIndex] = tempItems[oIndex + 1];
                tempItems[oIndex + 1] = item;
                setTempQuestion((question) => ({
                  ...question,
                  items: tempItems.map((item, index) => ({
                    ...item,
                    position: index,
                  })),
                }));
              }}
              isLast={optionIndex == tempQuestion.items.length - 1}
            />
          ))}
          <If cond={!sortAnswers}>
            <Button
              design="default-2"
              className="w-100"
              onClick={() => {
                tempQuestion.items.push({
                  ref_id: uuid.v4(),
                  type: "option",
                  items: [],
                  name: "",
                  price: 0,
                });
                setTempQuestion((question) => ({
                  ...question,
                  ...tempQuestion,
                }));
              }}
            >
              Adicionar resposta em texto
            </Button>
          </If>
        </If>
        <If cond={answerType === "withProducts"}>
          <Message>
            Este método simplesmente copia o nome dos produtos para as
            respostas, desta forma não há vinculo nenhum entre os dois
            elementos. Logo, caso hajam alterações no produto copiado, será
            necessário replicar essas alterações aqui.
          </Message>
          <Select
            design="two"
            className="flex-1 mt-20"
            label="Selecione o produto"
            value={selectedCategoryId}
            options={catalog.items.map((e) => ({ value: e.id, label: e.name }))}
            onChange={(e) => setSelectedCategoryId(e.target.value)}
          />
          <Button
            design="default-2"
            className="w-100 mt-20"
            disabled={!selectedCategoryId}
            onClick={() => {
              const selectedCategory = catalog.items.find(
                (c) => c.id == selectedCategoryId
              );
              if (selectedCategory) {
                const q = {
                  ...tempQuestion,
                  items: selectedCategory.items.map((p) => ({
                    parent: tempQuestion.id ? tempQuestion.id : null,
                    ref_id: uuid.v4(),
                    type: "option",
                    items: [],
                    name: p.name,
                    price: null,
                  })),
                };
                setTempQuestion(q);
                setAnswerType("withText");
              }
            }}
          >
            Adicionar respostas com produto
          </Button>
        </If>

        <If cond={answerType == null}>
          <div className={styles.addAnswer}>
            <Button
              design="default-2"
              className="w-100"
              onClick={() => {
                question.items.push({
                  ref_id: uuid.v4(),
                  type: "option",
                  items: [],
                  name: "",
                  price: 0,
                });
                if (question.id) {
                  question.items.forEach((o) => {
                    o.parent = question.id;
                  });
                }

                onChange(question);
                setAnswerType("withText");
              }}
            >
              Resposta em texto
            </Button>
            <Button
              design="default-2"
              className="w-100 mt-15"
              onClick={() => setAnswerType("withProducts")}
            >
              Resposta com produtos
            </Button>
          </div>
        </If>
      </div>

      <If cond={!sortAnswers}>
        <div className={styles.footerColumn}>
          <Button
            design="primary"
            className="w-100"
            onClick={() => {
              onChangeQuestion(tempQuestion);
              onClose();
            }}
            disabled={
              tempQuestion.items.find((q) => !q.name) ||
              !tempQuestion.name ||
              tempQuestion.items.length === 0
            }
          >
            Salvar etapa
          </Button>
          <If cond={tempQuestion.items.length > 1}>
            <Button
              design="primary-outline"
              className="w-100 mt-15"
              onClick={() => {
                setSortAnswers(true);
                originalQuestionRef.current = JSON.parse(
                  JSON.stringify(tempQuestion)
                );
              }}
            >
              Ordenar respostas
            </Button>
          </If>
        </div>
      </If>
      <If cond={sortAnswers}>
        <div className={styles.footer}>
          <Button
            design="primary"
            className="w-100"
            onClick={() => {
              onChangeQuestion(tempQuestion);
              setSortAnswers(false);
            }}
            disabled={
              tempQuestion.items.find((q) => !q.name) ||
              !tempQuestion.name ||
              tempQuestion.items.length === 0
            }
          >
            Salvar etapa
          </Button>
          <Button
            className="w-100 ml-15"
            onClick={() => {
              setSortAnswers(false);
              setTempQuestion(originalQuestionRef.current);
            }}
          >
            Cancelar
          </Button>
        </div>
      </If>
    </div>
  );
};

export const ProductStepQuestion = ({
  catalog,
  product,
  onChange,
  handleOptionSteps,
}) => {
  const [editMode, setEditMode] = useState(-1);
  const [sortMode, setSort] = useState(false);
  const [openAnswers, setOpenAnswers] = useState(false);
  const [questionToEditAnswers, setTempQuestionToEditAnswers] = useState(null);
  const [selectedStep, setSelectedStep] = useState(null);
  const [questioIndex, setTempQuestionIndex] = useState(null);
  const [stepConfig, setStepConfig] = useState(null);
  const [openFinished, setOpenFinished] = useState(false);

  const [sharedSteps, setSharedSteps] = useState([]);

  const getEmptyQuestion = (empty) => ({
    ref_id: uuid.v4(),
    name: empty ? "Nova etapa" : "",
    type: "question",
    items: [],
  });

  const handler = (e) => {
    onChange({ items: e });
  };

  const onDeleteQuestion = (questionIndex) => {
    handler(product.items.filter((e, eIdx) => eIdx != questionIndex));
  };

  const [holdingId, setHoldingId] = useState(null);

  useEffect(() => {
    const fetchHoldingId = async () => {
      const environment = await EnvironmentStorage.getEnvironment();
      setHoldingId(environment?.holdingId);
    };

    fetchHoldingId();
  }, []);

  useEffect(() => {
    loadSteps();
  }, [catalog]);

  const loadSteps = async () => {
    const environmentId = EnvironmentStorage.getSelectedEnvironment();
    const sharedStepsResponse = await CatalogAPI.getSharedSteps(environmentId);
    const sharedSteps = sharedStepsResponse.getData([]);

    setSharedSteps(sharedSteps);
  };

  const [step, setStep] = useState("ANSWERS");

  const buildStepOptions = (selectedOption) => {
    return sharedSteps.map((step) => ({
      value: step.id,
      label: step.name,
    }));
  };

  const handleSelectSharedStep = (stepId) => {
    const step = sharedSteps.find((s) => s.id == stepId);
    setSelectedStep(step);
  };

  const copySharedStep = (step) => {
    const builtStep = {
      ...step,
      id: uuid.v4(),
      name: step.name,
      type: "question",
      uuid: null,
      link_id: null,
      ref_id: uuid.v4(),
    };

    const newQuestions = [...product.items, builtStep];
    setTempQuestionIndex(newQuestions.length - 1);
    setTempQuestionToEditAnswers(builtStep);
  };

  const linkSharedStep = (step) => {
    const builtStep = {
      ...step,
      id: uuid.v4(),
      name: step.name,
      type: "question",
      uuid: null,
      link_id: step.id,
      ref_id: uuid.v4(),
    };

    const newQuestions = [...product.items, builtStep];
    setTempQuestionIndex(newQuestions.length - 1);
    setTempQuestionToEditAnswers(builtStep);
    handler(newQuestions);
  };

  return (
    <>
      <Label>Esse produto possui etapas?</Label>
      <Radio
        family="has-questions"
        value={false}
        checked={product.items.length > 0}
        className="mb-15"
        onChange={() => handler([])}
      >
        Não
      </Radio>
      <Radio
        family="has-questions"
        value={true}
        checked={product.items.length > 0}
        onChange={() => {
          const newQuestions = [getEmptyQuestion(true)];
          handler(newQuestions);
        }}
      >
        Sim
      </Radio>

      {product.items.length > 0 && (
        <>
          <Hairline />
          <div className={styles.container}>
            <div className="d-flex align-center justify-between relative">
              <If cond={!sortMode}>
                <Label className="mb-0">
                  Configure as etapas deste produto ({product.items.length})
                </Label>
              </If>
              <If cond={sortMode}>
                <Label className="mb-0">
                  Ordenar etapas ({product.items.length})
                </Label>
              </If>

              <div className="d-flex align-stretch gap-10">
                <If cond={sortMode}>
                  <Button design="default-3" onClick={() => setSort(false)}>
                    Salvar ordenação
                  </Button>
                </If>
                {product.items.length > 1 && (
                  <Button
                    design="default-icon"
                    onClick={() => setSort(!sortMode)}
                  >
                    <Icon name="sort-size-up-alt" size="lg" />
                  </Button>
                )}
              </div>
            </div>

            <Hairline className="mt-15 mb-15" />

            {product.items.map((questionItem, qIdx, questions) => {
              return (
                <QuestionItem
                  key={`question-item-${questionItem.ref_id}`}
                  mode={sortMode ? "sort" : editMode === qIdx ? "edit" : "show"}
                  catalog={catalog}
                  question={questionItem}
                  currentPosition={qIdx}
                  totalItems={questions.length}
                  onUpPosition={() => {
                    const upOption = questions[qIdx - 1];
                    questions[qIdx - 1] = { ...questionItem };
                    questions[qIdx] = { ...upOption };
                    handler(questions);
                  }}
                  onDownPosition={() => {
                    const downOption = questions[qIdx + 1];
                    questions[qIdx + 1] = { ...questionItem };
                    questions[qIdx] = { ...downOption };
                    handler(questions);
                  }}
                  onEditMode={(question) => {
                    setStep("ANSWERS");
                    setTempQuestionToEditAnswers(question);
                    setTempQuestionIndex(qIdx);
                    setOpenAnswers(true);
                  }}
                  onChange={(newQuestion) => {
                    questions[qIdx] = newQuestion;
                    handler(questions);
                  }}
                  onDelete={() => onDeleteQuestion(qIdx)}
                  visible={openAnswers}
                  onClearStepConfig={() => setStepConfig(null)}
                />
              );
            })}

            {editMode === -1 && !sortMode && (
              <Add
                onClick={() => {
                  const newQuestions = [...product.items, getEmptyQuestion()];
                  setTempQuestionIndex(newQuestions.length - 1);
                  setTempQuestionToEditAnswers(
                    newQuestions[newQuestions.length - 1]
                  );
                  setOpenAnswers(true);

                  setStep("CHOOSE_STEP_CONFIG");
                  setStepConfig(null);
                  // handler(newQuestions);
                }}
                text="Adicionar etapa"
                className="d-flex flex-column"
              />
            )}
          </div>

          <Drawer
            visible={openAnswers}
            title="Etapas"
            subtitle={product.name}
            showBackButton
            onClose={() => setOpenAnswers(false)}
            alreadyDrawer
            withoutTransition
          >
            <If cond={step === "CHOOSE_STEP_CONFIG"}>
              <div className={styles.options}>
                <Label>O que deseja?</Label>
                <div
                  className={styles.option}
                  onClick={() => {
                    setStep("CHOOSE_STEP");
                    setStepConfig("connect_step");
                  }}
                >
                  <ConnectStep />
                  <span>Conectar com etapa do banco</span>
                </div>
                <div
                  className={styles.option}
                  onClick={() => {
                    setStep("CHOOSE_STEP");
                    setStepConfig("copy_texts");
                  }}
                >
                  <ConnectStep1 />
                  <span>Copiar textos de etapa do banco</span>
                </div>
                <div
                  className={styles.option}
                  onClick={() => setStep("ANSWERS")}
                >
                  <ConnectStep2 />
                  <span>Criar nova etapa manualmente</span>
                </div>
              </div>
            </If>

            <If cond={step === "CHOOSE_STEP"}>
              <div className={styles.options}>
                <Label>O que deseja?</Label>
                <div className={styles.choose}>
                  <p className={styles.title}>SELECIONE A ETAPA DO BANCO</p>
                  <div className="d-flex align-stretch gap-10">
                    <Select
                      options={buildStepOptions(selectedStep)}
                      placeholder="Selecione a etapa"
                      value={selectedStep?.id}
                      onChange={(option) => {
                        handleSelectSharedStep(option.target.value);
                      }}
                      className="flex-1"
                    />
                    <Button
                      onClick={() => {
                        setSelectedStep(null);
                        setTempQuestionIndex(null);
                        setTempQuestionToEditAnswers(null);
                        setStepConfig(null);
                        setStep("CHOOSE_STEP_CONFIG");
                      }}
                    >
                      <Icon name="trash" light size="xl" />
                    </Button>
                  </div>
                </div>
              </div>

              <div className={styles.footer}>
                <Button
                  design="primary"
                  className="w-100"
                  disabled={!selectedStep}
                  onClick={() => {
                    if (stepConfig === "copy_texts") {
                      copySharedStep(selectedStep);
                      setStepConfig(null);
                      setStep("ANSWERS");
                    } else setOpenFinished(true);
                  }}
                >
                  Salvar etapa
                </Button>
              </div>
            </If>

            <If cond={step === "ANSWERS"}>
              <div className={styles.scrollContainer}>
                <If cond={stepConfig === "copy_texts"}>
                  <div className={styles.options}>
                    <Label>O que deseja?</Label>
                    <div className={styles.choose}>
                      <p className={styles.title}>SELECIONE A ETAPA DO BANCO</p>
                      <div className="d-flex align-stretch gap-10">
                        <Select
                          value={selectedStep}
                          disabled
                          className="flex-1"
                        />
                        <Button
                          onClick={() => {
                            setSelectedStep(null);
                            setTempQuestionIndex(null);
                            setTempQuestionToEditAnswers(null);
                            setStepConfig(null);
                            setStep("CHOOSE_STEP_CONFIG");
                          }}
                        >
                          <Icon name="trash" light size="xl" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </If>

                <Answers
                  holdingId={holdingId}
                  question={questionToEditAnswers}
                  catalog={catalog}
                  product={product}
                  onChange={(option) => {
                    const items = product.items.map((sItem) => {
                      return sItem;
                    });
                    onChange({ ...items, option });
                  }}
                  onChangeQuestion={(newQuestion) => {
                    const questions = product.items;
                    questions[questioIndex] = newQuestion;
                    setTempQuestionToEditAnswers(newQuestion);

                    handler(questions);
                  }}
                  onDiscardQuestion={() => {
                    const questions = product.items.slice(
                      0,
                      product.items.length - 1
                    );

                    handler(questions);
                    setOpenAnswers(false);
                  }}
                  onDelete={(question, oIndex) => {
                    question.items = question.items.filter((o, oIdx) => {
                      return oIdx !== oIndex;
                    });

                    onChange(question);
                  }}
                  onClose={() => setOpenAnswers(false)}
                  handleOptionSteps={(option) => handleOptionSteps(option)}
                />
              </div>
            </If>
          </Drawer>

          {editMode > -1 && (
            <div className="d-flex w-100 mt-20">
              <Button
                design="primary"
                className="flex-1"
                onClick={() => setEditMode(-1)}
              >
                Confirmar
              </Button>
              <Button
                design="default-icon"
                onClick={() => onDeleteQuestion(editMode)}
                className="ml-10"
              >
                <Icon name="trash" size="xl" />
              </Button>
            </div>
          )}
        </>
      )}

      <Drawer
        visible={openFinished}
        withoutTransition
        onClose={() => {
          setOpenFinished(false);
          setOpenAnswers(false);
        }}
        title={`Etapa cadastrada com sucesso`}
        subtitle="Você já pode encontrar esta etapa cadastrada na edição deste produto"
      >
        <div className={styles.successIcon}>
          <SuccessIcon />
        </div>
        <div className={styles.footer}>
          <Button
            design="default-3"
            className="w-100"
            onClick={() => {
              linkSharedStep(selectedStep);
              setStepConfig(null);
              setOpenFinished(false);
              setOpenAnswers(false);
              setStep("ANSWERS");
            }}
          >
            Ver etapas
          </Button>
        </div>
      </Drawer>
    </>
  );
};
